import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { filter, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  data?: any;
}

@Component({
    selector: 'app-debug-console',
    imports: [CommonModule, TranslatePipe],
    template: `
    <div class="debug-console" *ngIf="isVisible" [class.collapsed]="isCollapsed">
      <div class="debug-header" (click)="toggleCollapse()">
        <span class="debug-title">{{ 'errors.debug.console' | translate }}</span>
        <div class="debug-actions">
          <button class="debug-btn" (click)="clearLogs($event)" [title]="'errors.debug.clearLogs' | translate">
            🗑️ LIMPAR
          </button>
          <button class="debug-btn" (click)="toggleCollapse($event)" [title]="(isCollapsed ? 'errors.debug.maximize' : 'errors.debug.minimize') | translate">
            {{ isCollapsed ? '▲' : '▼' }}
          </button>
        </div>
      </div>
      <div class="debug-content" *ngIf="!isCollapsed">
        <div class="debug-logs" #logsContainer>
          <div 
            *ngFor="let log of logs" 
            [class]="'log-entry log-' + log.level"
          >
            <span class="log-time">{{ log.timestamp }}</span>
            <span class="log-level">{{ log.level.toUpperCase() }}</span>
            <span class="log-message">{{ log.message }}</span>
            <pre class="log-data" *ngIf="log.data">{{ formatData(log.data) }}</pre>
          </div>
        </div>
      </div>
      <div class="debug-footer" *ngIf="!isCollapsed">
        <span class="log-count">{{ logs.length }} logs</span>
      </div>
    </div>
  `,
    styles: [`
    .debug-console {
      position: fixed;
      bottom: 20px;
      left: 20px;
      width: 600px;
      max-height: 500px;
      background: rgba(0, 0, 0, 0.95);
      border: 2px solid var(--color-blue-600);
      border-radius: 8px;
      color: #fff;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      z-index: 99999;
      display: flex;
      flex-direction: column;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
      transition: all 0.3s ease;
    }

    .debug-console.collapsed {
      max-height: 40px;
    }

    .debug-header {
      background: var(--color-blue-600);
      padding: 8px 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      user-select: none;
    }

    .debug-title {
      font-weight: bold;
      color: #fff;
    }

    .debug-actions {
      display: flex;
      gap: 8px;
    }

    .debug-btn {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
      transition: background 0.2s;
    }

    .debug-btn:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    .debug-content {
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .debug-logs {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      max-height: 400px;
    }

    .log-entry {
      margin-bottom: 8px;
      padding: 6px;
      border-left: 3px solid #666;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 2px;
      word-wrap: break-word;
    }

    .log-entry.log-log {
      border-left-color: var(--color-success);
    }

    .log-entry.log-info {
      border-left-color: var(--color-info);
    }

    .log-entry.log-warn {
      border-left-color: var(--color-warning);
    }

    .log-entry.log-error {
      border-left-color: var(--color-error);
      background: rgba(244, 67, 54, 0.1);
    }

    .log-time {
      color: #888;
      margin-right: 8px;
      font-size: 10px;
    }

    .log-level {
      font-weight: bold;
      margin-right: 8px;
      font-size: 10px;
    }

    .log-level.log-log { color: var(--color-success); }
    .log-level.log-info { color: var(--color-info); }
    .log-level.log-warn { color: var(--color-warning); }
    .log-level.log-error { color: var(--color-error); }

    .log-message {
      color: #fff;
    }

    .log-data {
      margin-top: 4px;
      margin-left: 20px;
      color: #aaa;
      font-size: 11px;
      white-space: pre-wrap;
      max-height: 200px;
      overflow-y: auto;
      background: rgba(0, 0, 0, 0.3);
      padding: 4px;
      border-radius: 2px;
    }

    .debug-footer {
      background: rgba(0, 0, 0, 0.5);
      padding: 4px 12px;
      font-size: 10px;
      color: #888;
      text-align: center;
    }

    .log-count {
      color: #888;
    }

    
    .debug-logs::-webkit-scrollbar {
      width: 8px;
    }

    .debug-logs::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
    }

    .debug-logs::-webkit-scrollbar-thumb {
      background: var(--color-blue-600);
      border-radius: 4px;
    }

    .debug-logs::-webkit-scrollbar-thumb:hover {
      background: var(--color-blue-700);
    }
  `]
})
export class DebugConsoleComponent implements OnInit, OnDestroy {
  @ViewChild('logsContainer', { static: false }) logsContainer!: ElementRef;

  logs: LogEntry[] = [];
  isVisible = false;
  isCollapsed = false;
  private originalConsole: any = {};
  private maxLogs = 1000;
  private storageKey = 'autolac_debug_logs';
  private routeSubscription?: Subscription;

  constructor(
    private router: Router
  ) {}

  ngOnInit(): void {
    // Segurança (WI 59972): o painel de depuração e a persistência de logs existem SOMENTE em
    // Desenvolvimento. Em Homologação/Produção (enableDebugTools=false) o parâmetro ?debugMode=true
    // NÃO ativa nada, o console não é interceptado e nada é salvo no armazenamento local. Além disso,
    // limpamos qualquer histórico legado que tenha ficado no localStorage de versões anteriores.
    if (!environment.enableDebugTools) {
      try {
        localStorage.removeItem(this.storageKey);
      } catch {
        /* localStorage indisponível — ignorar */
      }
      return;
    }

    // Verifica o parâmetro debugMode na URL inicial
    this.checkDebugMode();

    // Observa mudanças na rota para verificar o parâmetro debugMode
    this.routeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkDebugMode();
      });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.isVisible) {
      this.restoreConsole();
    }
  }

  private checkDebugMode(): void {
    // Verifica se debugMode=true está presente na URL
    // Usa Router para obter os query params da rota atual
    const urlTree = this.router.parseUrl(this.router.url);
    const debugMode = urlTree.queryParams['debugMode'];
    const shouldShow = debugMode === 'true' || debugMode === true;

    if (shouldShow && !this.isVisible) {
      // Ativa o console de debug
      this.isVisible = true;
      this.loadLogsFromStorage();
      this.interceptConsole();
    } else if (!shouldShow && this.isVisible) {
      // Desativa o console de debug
      this.isVisible = false;
      this.restoreConsole();
    }
  }

  private interceptConsole(): void {

    this.originalConsole.log = console.log;
    this.originalConsole.warn = console.warn;
    this.originalConsole.error = console.error;
    this.originalConsole.info = console.info;

    console.log = (...args: any[]) => {
      this.addLog('log', args);
      this.originalConsole.log.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      this.addLog('warn', args);
      this.originalConsole.warn.apply(console, args);
    };

    console.error = (...args: any[]) => {
      this.addLog('error', args);
      this.originalConsole.error.apply(console, args);
    };

    console.info = (...args: any[]) => {
      this.addLog('info', args);
      this.originalConsole.info.apply(console, args);
    };
  }

  private restoreConsole(): void {
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
  }

  private addLog(level: 'log' | 'warn' | 'error' | 'info', args: any[]): void {

    let message = '';
    let data: any[] = [];

    args.forEach(arg => {
      if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') {
        message += (message ? ' ' : '') + String(arg);
      } else if (arg === null || arg === undefined) {
        message += (message ? ' ' : '') + String(arg);
      } else {

        data.push(arg);
      }
    });

    const logEntry: LogEntry = {
      timestamp: new Date().toLocaleTimeString('pt-BR'),
      level,
      message: message || (data.length > 0 ? '[Objeto]' : '[Sem mensagem]'),
      data: data.length > 0 ? data : undefined
    };

    this.logs.push(logEntry);

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.saveLogsToStorage();

    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  private scrollToBottom(): void {
    if (this.logsContainer) {
      const element = this.logsContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  private saveLogsToStorage(): void {
    try {

      const logsToSave = this.logs.slice(-500);
      localStorage.setItem(this.storageKey, JSON.stringify(logsToSave));
    } catch (error) {

      this.logs = this.logs.slice(-100);
      localStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    }
  }

  private loadLogsFromStorage(): void {
    try {
      const savedLogs = localStorage.getItem(this.storageKey);
      if (savedLogs) {
        this.logs = JSON.parse(savedLogs);

        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      }
    } catch (error) {
      console.error('Erro ao carregar logs do storage:', error);
    }
  }

  clearLogs(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.logs = [];
    localStorage.removeItem(this.storageKey);
    this.originalConsole.log('Debug Console: Logs limpos');
  }

  toggleCollapse(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isCollapsed = !this.isCollapsed;
  }

  formatData(data: any): string {
    try {
      if (Array.isArray(data) && data.length === 0) {
        return '';
      }
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return String(data);
    }
  }
}
